import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../components/CartContext';
import { X } from 'lucide-react';

export default function CartPage() {
  const [couponCode, setCouponCode] = useState('');
  const [shippingMethod, setShippingMethod] = useState('self-collection');
  const navigate = useNavigate();
  
  // Use CartContext instead of local state
  const { cartItems, updateQuantity, removeFromCart, getCartTotal } = useCart();
  
  const subtotal = getCartTotal();
  const shippingCost = shippingMethod === 'tracked-mail' ? 5.00 : 0;
  const total = subtotal + shippingCost;

  const handleCheckout = () => {
    // Navigate to checkout page with shipping method
    navigate('/checkout', { state: { shippingMethod } });
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">Your cart is empty</h1>
          <p className="text-gray-600 mb-8">Add some items to get started!</p>
          <button 
            onClick={() => navigate('/events')}
            className="bg-red-400 hover:bg-red-500 text-white px-6 py-3 rounded font-medium transition-colors"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-6xl mx-auto px-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-8">View cart</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Cart Items Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm">
              {/* Desktop Table Header - Hidden on mobile */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-gray-200 text-sm font-medium text-gray-600">
                <div className="col-span-5">Product</div>
                <div className="col-span-2 text-center">Quantity</div>
                <div className="col-span-2 text-center">Price</div>
                <div className="col-span-2 text-right">Subtotal</div>
                <div className="col-span-1"></div>
              </div>
              
              {/* Cart Items */}
              <div className="divide-y divide-gray-200">
                {cartItems.map((item, index) => (
                  <div key={`${item.id}-${item.size}-${index}`}>
                    {/* Desktop Layout */}
                    <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 items-center">
                      <div className="col-span-5 flex items-center gap-4">
                        <img 
                          src={item.image} 
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded"
                        />
                        <div>
                          <span className="text-gray-700 font-medium block">{item.name}</span>
                          {item.size && <span className="text-gray-500 text-sm">Size: {item.size}</span>}
                        </div>
                      </div>
                      <div className="col-span-2 flex justify-center">
                        <input 
                          type="number" 
                          value={item.quantity}
                          onChange={(e) => updateQuantity(item.id, item.size, parseInt(e.target.value) || 1)}
                          className="w-20 px-3 py-2 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                          min="1"
                        />
                      </div>
                      <div className="col-span-2 text-center text-gray-700">
                        ${item.price.toFixed(2)}
                      </div>
                      <div className="col-span-2 text-right text-gray-700 font-medium">
                        ${(item.price * item.quantity).toFixed(2)}
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button 
                          onClick={() => removeFromCart(item.id, item.size)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Mobile Layout */}
                    <div className="md:hidden p-4">
                      <div className="flex gap-4">
                        <img 
                          src={item.image} 
                          alt={item.name}
                          className="w-24 h-24 object-cover rounded flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 min-w-0 pr-2">
                              <h3 className="text-gray-700 font-medium truncate">{item.name}</h3>
                              {item.size && <p className="text-gray-500 text-sm">Size: {item.size}</p>}
                            </div>
                            <button 
                              onClick={() => removeFromCart(item.id, item.size)}
                              className="text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-600">Price:</span>
                              <span className="text-gray-700 font-medium">${item.price.toFixed(2)}</span>
                            </div>
                            
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 text-sm">Quantity:</span>
                              <input 
                                type="number" 
                                value={item.quantity}
                                onChange={(e) => updateQuantity(item.id, item.size, parseInt(e.target.value) || 1)}
                                className="w-20 px-3 py-2 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                min="1"
                              />
                            </div>
                            
                            <div className="flex justify-between items-center text-sm pt-2 border-t border-gray-100">
                              <span className="text-gray-600 font-medium">Subtotal:</span>
                              <span className="text-gray-700 font-bold">${(item.price * item.quantity).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Coupon Section */}
              <div className="px-4 sm:px-6 py-4 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <input 
                    type="text"
                    placeholder="Coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button className="px-6 py-2 bg-red-400 hover:bg-red-500 text-white rounded text-sm font-medium transition-colors whitespace-nowrap">
                    Apply coupon
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Order Summary Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              {/* Subtotal */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <span className="text-gray-600">Subtotal</span>
                <span className="text-gray-800 font-medium">${subtotal.toFixed(2)}</span>
              </div>
              
              {/* Shipping */}
              <div className="py-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-600">Shipping</span>
                </div>
                
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input 
                      type="radio" 
                      name="shipping"
                      value="self-collection"
                      checked={shippingMethod === 'self-collection'}
                      onChange={(e) => setShippingMethod(e.target.value)}
                      className="w-4 h-4 text-red-500 focus:ring-red-500 flex-shrink-0"
                    />
                    <span className="text-gray-700 text-sm">Self-Collection (Carnival)</span>
                  </label>
                  
                  {/* <label className="flex items-center gap-3 cursor-pointer">
                    <input 
                      type="radio" 
                      name="shipping"
                      value="tracked-mail"
                      checked={shippingMethod === 'tracked-mail'}
                      onChange={(e) => setShippingMethod(e.target.value)}
                      className="w-4 h-4 text-red-500 focus:ring-red-500 flex-shrink-0"
                    />
                    <div className="flex items-center justify-between flex-1">
                      <span className="text-gray-700 text-sm">Tracked Mail</span>
                      <span className="text-gray-700 text-sm font-medium">$5.00</span>
                    </div>
                  </label> */}
                </div>
                
                <p className="text-xs text-gray-500 mt-3">
                  Shipping options will be updated during checkout.
                </p>
              </div>
              
              {/* Total */}
              <div className="flex items-center justify-between py-4">
                <span className="text-gray-800 font-medium">Total</span>
                <span className="text-gray-800 font-bold text-lg">${total.toFixed(2)}</span>
              </div>
              
              {/* Checkout Button */}
              <button 
                onClick={handleCheckout}
                className="w-full py-3 bg-red-400 hover:bg-red-500 text-white rounded font-medium transition-colors"
              >
                Proceed to checkout
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}